import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X, PlusSquare } from "lucide-react";

const DISMISS_KEY = "scm_install_dismissed_at";
const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const recentlyDismissed = () =>
  Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < DISMISS_MS;

export default function InstallAppBanner() {
  const location = useLocation();
  const [prompt, setPrompt] = useState(() => window.__scmInstallPrompt || null);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(
    () => isStandalone() || !!window.ReactNativeWebView || recentlyDismissed()
  );

  useEffect(() => {
    const onAvailable = () => setPrompt(window.__scmInstallPrompt);
    const onDone = () => setHidden(true);
    window.addEventListener("scm-install-available", onAvailable);
    window.addEventListener("scm-install-done", onDone);
    const t = setTimeout(() => setReady(true), 3000);
    return () => {
      window.removeEventListener("scm-install-available", onAvailable);
      window.removeEventListener("scm-install-done", onDone);
      clearTimeout(t);
    };
  }, []);

  const ios = isIOS();
  if (hidden || !ready || location.pathname.startsWith("/owner")) return null;
  if (!prompt && !ios) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  const install = async () => {
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__scmInstallPrompt = null;
    setPrompt(null);
    if (outcome === "accepted") setHidden(true);
    else dismiss();
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-[60] rounded-2xl bg-white border border-[#E5E2D9] shadow-[0_12px_40px_rgba(42,51,44,0.18)] p-4 flex gap-3 items-start animate-in slide-in-from-bottom-4 fade-in duration-500"
      data-testid="install-app-banner"
      role="dialog"
      aria-label="Install Shop, Cal & Meal"
    >
      <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0 border border-[#E5E2D9]" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#2A332C] text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
          Install Shop, Cal & Meal
        </div>
        {ios ? (
          <p className="text-xs text-[#5C6B61] mt-1 leading-relaxed" data-testid="install-ios-steps">
            Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Share" /> Share, then{" "}
            <PlusSquare className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Add" /> <b>Add to Home Screen</b> for a full-screen app.
          </p>
        ) : (
          <>
            <p className="text-xs text-[#5C6B61] mt-1">Add it to your home screen. Opens full-screen, no app store needed.</p>
            <button
              onClick={install}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#3B5A3E] text-white text-xs font-medium px-4 py-2 transition-colors hover:bg-[#2F4932] active:scale-95"
              data-testid="install-app-btn"
            >
              <Download className="w-3.5 h-3.5" /> Install app
            </button>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        className="p-1 rounded-full text-[#5C6B61] hover:bg-[#F2F0EA] transition-colors"
        aria-label="Dismiss"
        data-testid="install-app-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
